import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, CONTACT } from "../data";
import { useSectionNav } from "../hooks/useSectionNav";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goSection = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  const handleNav = (e, href) => {
    e.preventDefault();
    goSection(href.replace("#", ""), close);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-100 shadow-subtle"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Logo onClick={close} />

          <nav className="hidden lg:flex items-center gap-7" aria-label="Meniu principal">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={`/${link.href}`}
                onClick={(e) => handleNav(e, link.href)}
                data-testid={`nav-${link.href.replace("#", "")}`}
                className="text-sm font-medium text-ink-muted hover:text-ink relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-brand hover:after:w-full after:transition-all after:duration-300 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:block">
            <a
              href={CONTACT.phoneHref}
              data-testid="header-contact-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-subtle hover:bg-brand-hover hover:shadow-card transition-[background-color,box-shadow] duration-300"
            >
              <Phone className="h-4 w-4" />
              Contactează-ne
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-ink hover:bg-gray-100 transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="Deschide meniul"
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-card" data-testid="mobile-menu">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={`/${link.href}`}
                onClick={(e) => handleNav(e, link.href)}
                data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                className="px-3 py-3 rounded-lg text-base font-medium text-ink-muted hover:text-ink hover:bg-gray-50 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href={CONTACT.phoneHref}
              onClick={close}
              data-testid="mobile-header-contact-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white"
            >
              <Phone className="h-4 w-4" />
              Contactează-ne
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
